import {
  Badge,
  Box,
  Card,
  CardBody,
  HStack,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';

/** Format YYYY-MM-DD as a short mobile-friendly "MMM D" string. */
function formatDateMobile(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Format YYYY-MM-DD into `{ weekday, date }` for the desktop card layout. */
function formatDateDesktop(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
    date: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  };
}

/**
 * Responsive grid of `[dateStr, count]` cancellation openings. Renders a card
 * grid on desktop and a stacked list on mobile. Shared between the blog-page
 * NZ Great Walks stats card and the TripBuilder walk-level banner detail.
 */
export default function CancellationDateGrid({
  entries,
  colorScheme = 'brand',
  unitLabel = 'bed',
}) {
  if (!entries?.length) return null;
  const plural = (n) => (n === 1 ? unitLabel : `${unitLabel}s`);

  return (
    <>
      <Box display={['none', 'block']} w="full">
        <SimpleGrid columns={[2, 3, 4]} spacing={[2, 3, 4]} w="full">
          {entries.map(([dateStr, count]) => {
            const { weekday, date } = formatDateDesktop(dateStr);
            return (
              <Card
                key={dateStr}
                bg="white"
                shadow="sm"
                border="1px solid"
                borderColor={`${colorScheme}.100`}
                _hover={{ shadow: 'md', borderColor: `${colorScheme}.300` }}
                transition="all 0.2s"
              >
                <CardBody p={[2, 3, 4]} textAlign="center">
                  <Text fontSize={['xs', 'xs', 'sm']} color="gray.500" mb={0.5}>
                    {weekday}
                  </Text>
                  <Text
                    fontSize={['xs', 'sm', 'md']}
                    fontWeight="bold"
                    color="gray.800"
                  >
                    {date}
                  </Text>
                  <Badge
                    colorScheme={colorScheme}
                    mt={1.5}
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                  >
                    {count} {plural(count)}
                  </Badge>
                </CardBody>
              </Card>
            );
          })}
        </SimpleGrid>
      </Box>

      <Box
        display={['block', 'none']}
        w="full"
        bg="white"
        borderRadius="lg"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
      >
        {entries.map(([dateStr, count], index) => (
          <HStack
            key={dateStr}
            justify="space-between"
            px={3}
            py={2}
            borderBottom={index < entries.length - 1 ? '1px solid' : 'none'}
            borderColor="gray.100"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              {formatDateMobile(dateStr)}
            </Text>
            <HStack spacing={1}>
              <Text fontSize="sm" fontWeight="bold" color={`${colorScheme}.600`}>
                {count}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {plural(count)}
              </Text>
            </HStack>
          </HStack>
        ))}
      </Box>
    </>
  );
}
